// @flow
import {
  Fragment,
  useEffect,
  useState,
  useMemo,
  useCallback
} from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';

import DyorDataWrapper from '@components/DyorDataWrapper';

import type {
  DiamondType,
  RecentlyViewedDiamondSummaryType
} from '@types/diamond';
import type { ObjectType } from '@types/misc';

import DesignYourOwnRing from '@components/dyor/Container';
import ItemHeader from '@components/productDetail/ItemHeader';
import ShippingInfo from '@components/productDetail/ShippingInfo';
import DiamondTables from '@components/productDetail/DiamondTables';
import ProductImages from '@components/productDetail/DiamondProductImages';
import RecentlyViewed from '@components/RecentlyViewedDiamonds';
import PageFooter from '@components/productDetail/PageFooter.jsx';
import DyorDiamondActions from '@components/productDetail/DyorDiamondActions';
import LooseDiamondActions from '@components/productDetail/LooseDiamondActions';

import ViewItemEvent from '../productDetail/googleAnalyticsEvents/viewItem/diamond';

import { CompatibilityProvider } from '../../contexts/compatibility';

import LinkPopup from '@components/popups/Link';
import * as LooseDiamondContent from '@components/popups/content/LooseDiamondPackaging';

import { getDiamondDescription } from '@src/utils/diamonds';
import SettingCompatibility from '../productDetail/diamonds/SettingCompatibility';

import Specifications from '@components/productDetail/diamonds/Specifications';

import css from '@styles/productDetail';
import LumeraPersistGate from '../LumeraPersistGate';
import { RailsContextType } from '../../types/misc';
import useProductDetailColumnCount from '../../hooks/useProductDetailColumnCount';

type PropsType = {
  diamond: DiamondType,
  dyor: boolean,
  railsContext: RailsContextType
};

function AddToRecentlyViewed({ diamond }) {
  const dispatch = useDispatch();

  useEffect(() => {
    if (diamond) {
      dispatch({ type: 'PUSH_RECENTLY_VIEWED_DIAMOND', item: diamond });
    }
  }, [diamond, dispatch]);

  return null;
}

function DescriptionAndPrice({
  diamond,
  dyor
}: {
  diamond: DiamondType,
  dyor: boolean
}) {
  return (
    <div className={`${css.description} gray-links`}>
      <ItemHeader
        title={getDiamondDescription(diamond)}
        price={diamond.retail_price}
        bank_wire={diamond.bank_wire}
      />
      <div style={{ marginTop: 10 }}>
        This {diamond.final_cut_grade} cut, {diamond.color}-color,{' '}
        {diamond.clarity}
        -clarity {diamond.natural ? '' : 'lab-grown'} diamond comes with a GIA
        certificate, our Lifetime Trade-In and Quality Guarantee, appraisal,
        30-day returns, and free shipping. This diamond may be{' '}
        {dyor || diamond.has_compatible_settings
          ? 'placed in a setting, or'
          : null}{' '}
        purchased{' '}
        <LinkPopup initialContent={LooseDiamondContent}>loose</LinkPopup>.
      </div>
    </div>
  );
}

function SingleColumn({
  dyor,
  diamond,
  recentlyViewedDiamonds,
  hoveredSpec,
  setHoveredSpec,
  isFavorited,
  onFavoriteToggle
}: {
  dyor: boolean,
  diamond: DiamondType,
  recentlyViewedDiamonds: Array<RecentlyViewedDiamondSummaryType>,
  hoveredSpec: string | null,
  onFavoriteToggle: () => void,
  isFavorited: boolean
}) {
  return (
    <div className={`${css.container} product-detail--container diamond`}>
      <div className={css.singleColumn}>
        <DescriptionAndPrice diamond={diamond} dyor={dyor} />
        {dyor ? null : <ShippingInfo deliveryDate={diamond.delivery_date} />}
        {dyor ? (
          <DyorDiamondActions diamond={diamond} />
        ) : (
          <LooseDiamondActions diamond={diamond} />
        )}
        <ProductImages
          type={'diamond'}
          diamond={diamond}
          images={[{}]}
          hoveredSpec={hoveredSpec}
          showInARingButton={!dyor}
          onFavoriteToggle={onFavoriteToggle}
          isFavorited={isFavorited}
          dyor={dyor}
        />
        <Specifications
          diamond={diamond}
          onHover={setHoveredSpec}
          omitDeliveryDate={dyor}
        />
        <RecentlyViewed items={recentlyViewedDiamonds} />
      </div>
      <DiamondTables diamond={diamond} />
    </div>
  );
}

function MultipleColumns({
  columns,
  dyor,
  diamond,
  recentlyViewedDiamonds,
  hoveredSpec,
  setHoveredSpec,
  isFavorited,
  onFavoriteToggle
}: {
  columns: number,
  dyor: boolean,
  diamond: DiamondType,
  recentlyViewedDiamonds: Array<RecentlyViewedDiamondSummaryType>,
  hoveredSpec: string | null,
  setHoveredSpec: (S: string) => void,
  onFavoriteToggle: () => void,
  isFavorited: boolean
}) {
  return (
    <div className={`${css.container} product-detail--container diamond`}>
      <div className={css.leftAndMiddle}>
        <div className={css.left}>
          {columns > 2 ? (
            <Specifications
              diamond={diamond}
              onHover={setHoveredSpec}
              omitDeliveryDate={dyor}
            />
          ) : null}
        </div>
        <div className={css.middle}>
          <ProductImages
            type={'diamond'}
            diamond={diamond}
            images={[{}]}
            hoveredSpec={hoveredSpec}
            showInARingButton={!dyor}
            onFavoriteToggle={onFavoriteToggle}
            isFavorited={isFavorited}
            dyor={dyor}
          />
          <RecentlyViewed items={recentlyViewedDiamonds} />
          {columns === 2 ? (
            <Specifications
              diamond={diamond}
              onHover={setHoveredSpec}
              omitDeliveryDate={dyor}
            />
          ) : null}
        </div>
      </div>
      <div className={css.right}>
        <DescriptionAndPrice diamond={diamond} dyor={dyor} />
        {dyor ? null : <ShippingInfo deliveryDate={diamond.delivery_date} />}
        {dyor ? (
          <DyorDiamondActions diamond={diamond} />
        ) : (
          <LooseDiamondActions diamond={diamond} />
        )}
        <DiamondTables diamond={diamond} />
      </div>
    </div>
  );
}

function DiamondDetail(props: PropsType) {
  const { serverSide } = props.railsContext;
  const [hoveredSpec, setHoveredSpec] = useState(null);
  const [ssr, setSsr] = useState(true);
  const dispatch = useDispatch();

  const favoritedDiamonds = useSelector(state => state.diamondComparison.ids);

  const diamondIsFavorited = useMemo(() => {
    return (
      favoritedDiamonds.indexOf(props.diamond.list_service_diamond_id) !== -1
    );
  }, [favoritedDiamonds, props.diamond.list_service_diamond_id]);

  const state = useSelector(state => {
    return {
      // the first item in the array is item currently being viewed -- remove it
      recentlyViewedDiamonds: state.recentlyViewedDiamonds.slice(1)
    };
  });

  const handleFavoriteToggle = useCallback(() => {
    dispatch({
      type: 'TOGGLE_DIAMOND_FOR_COMPARISON',
      item: props.diamond.list_service_diamond_id
    });
  }, [dispatch, props.diamond.list_service_diamond_id]);
  const columns = useProductDetailColumnCount();

  useEffect(() => {
    setSsr(serverSide);
  }, [serverSide]);

  return (
    <CompatibilityProvider>
      <LumeraPersistGate>
        <AddToRecentlyViewed diamond={props.diamond} />
      </LumeraPersistGate>
      <div>
        {props.dyor ? (
          <DyorDataWrapper>
            <DesignYourOwnRing />
          </DyorDataWrapper>
        ) : null}

        {props.dyor ? (
          <SettingCompatibility
            diamondId={props.diamond.list_service_diamond_id}
          />
        ) : null}

        {!ssr && <ViewItemEvent diamond={props.diamond} />}

        {props.diamond.carat ? (
          <Fragment>
            {ssr || columns > 1 ? (
              <MultipleColumns
                columns={columns}
                dyor={props.dyor}
                diamond={props.diamond}
                recentlyViewedDiamonds={state.recentlyViewedDiamonds}
                hoveredSpec={hoveredSpec}
                setHoveredSpec={setHoveredSpec}
                onFavoriteToggle={handleFavoriteToggle}
                isFavorited={diamondIsFavorited}
              />
            ) : (
              <SingleColumn
                dyor={props.dyor}
                diamond={props.diamond}
                recentlyViewedDiamonds={state.recentlyViewedDiamonds}
                hoveredSpec={hoveredSpec}
                setHoveredSpec={setHoveredSpec}
                onFavoriteToggle={handleFavoriteToggle}
                isFavorited={diamondIsFavorited}
              />
            )}
          </Fragment>
        ) : null}
        <PageFooter />
      </div>
    </CompatibilityProvider>
  );
}

DiamondDetail.defaultProps = {
  dyor: true
};

const DiamondDetailWithStore = (
  props: ObjectType,
  railsContext: RailsContextType
) =>
  () => {
    const store = ReactOnRails.getStore('SharedReduxStore');

    return (
      <Provider store={store}>
        <DiamondDetail {...props} railsContext={railsContext} />
      </Provider>
    );
  };

export default DiamondDetailWithStore;
